<template>
	<div>

		<!-- staff -->
		<div id='profile-staff' class='profile-section'>
			<div class='container'>
				<div class='section'>
					<div class='columns'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<div class='profile-section-title-div' id='staff-title'>
									<p class='profile-h1'>School Staff</p>
									<img class='profile-icon filter-pink-dark' src="@/assets/icon-teacher.svg" alt='Teacher by Piotrek Chuchla from the Noun Project' />
							</div>
						</div>
					</div>

					<div class='columns is-centered'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<p> 
								School staff should be as diverse as their students.
							</p>
							<br>
							<p>
								A recent study in North Carolina showed that low-income Black students who had at least one Black teacher in elementary school were significantly more likely to graduate high school and consider attending college.
							</p>
						</div>

						<div class='column is-8-widescreen is-6-tablet' >
							<div id='chart-sizing-div'>
								<p class='chart-title has-text-pink-dark'>Do teachers in this district have the same race/ethnicity as students?</p>
								<p class='chart-subtitle'>This chart compares the percentage of teachers and students who identify as a particular race/ethnicity</p>
								<EquityChart 
									metric="pct_teachers_EQLOG" 
									:metricDescArr="[
										{'metric': 'pct_teachers', 'text': 'teachers'},
										{'metric': 'pct_students', 'text': 'students'}
									]"
									:width="innerWidth" 
									colorClass="has-text-pink-dark"
								/>
								<div class='chart-footnote'>
									Demographic data is reported by the NC Department of Public Instruction for the 2018-19 school year.<a href="http://apps.schools.nc.gov/ords/f?p=145:15:::NO:::"><sup>1</sup></a> <a href="http://apps.schools.nc.gov/ords/f?p=145:109:::NO:::"><sup>2</sup></a>
								</div>
								<div class='chart-footnote'>
									The state only publishes data for the race/ethnicity groups "Black", "Other", and "White". If a group is missing from the chart this means that the district did not have enough data to report that group.
								</div>
							</div>
						</div>

					</div>
				</div>
			</div>
		</div>

		<!-- academics -->
		<div id='profile-academics' class='profile-section'>
			<div class='container'>
				<div class='section'>
					<div class='columns'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<div class='profile-section-title-div' id='academics-title'>
								<p class='profile-h1'>Academic Achievement</p>
								<img class='profile-icon filter-purple-dark' src="@/assets/icon-test.svg" alt='Test by Adrien Coquet from the Noun Project'>
							</div>
						</div>
					</div>

					<div class='columns is-centered'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<p> 
								Low test scores can negatively impact a student’s overall academic opportunities and outcomes.
							</p>
							<br>
							<p>
								While standardized test scores are not a reliable measure of true ability for all students, educators use them for many important decisions like placing students in courses, promoting students to the next grade, or identifying students as gifted. Students who are behind academically are also more likely to have attendance issues, experience behavior struggles, and drop out of school.
							</p>
						</div>

						<div class='column is-8-widescreen is-6-tablet'>
							<div class='academic-chart'>
								<p class='chart-title has-text-purple-dark'>Is this district preparing all students for academic success?</p>
								<p class='chart-subtitle'>This chart shows the percentage of elementary students scoring "college and career ready" on state tests</p>
								<BarChart metric="ccready_3_8_pct_4+" :width="innerWidth" colorClass="has-text-purple-dark"/>
							</div>
							<div>
								<p class='chart-subtitle'>This chart shows the percentage of high school students scoring "college and career ready" on state tests</p>
								<BarChart metric="ccready_9_12_pct_4+" :width="innerWidth" colorClass="has-text-purple-dark"/>
							</div>

							<div class='chart-footnote'>
								Academic data is reported by the NC Department of Public Instruction for the 2018-19 school year.<a href="https://www.dpi.nc.gov/districts-schools/testing-and-school-accountability/school-accountability-and-reporting/accountability-data-sets-and-reports"><sup>3</sup></a>
							</div>
							<div class='chart-footnote'>
								This chart only includes races and ethnicities for which the district has enough data to report. Percentages greater than 95 or less than 5 are reported by the state as &gt;95 and &lt;5 respectively. Here they are displayed as 95 and 5.
							</div>
						</div>

					</div>
				</div>
			</div>
		</div>

		<!-- discipline -->
		<div id='profile-discipline' class='profile-section'>
			<div class='container'>
				<div class='section'>
					<div class='columns'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<div class='profile-section-title-div' id='discipline-title'>
								<p class='profile-h1'>School Discipline</p>
								<img class='profile-icon filter-pink-darkest' src="@/assets/icon-discipline.svg" alt='Error by Deemak Daksina from the Noun Project'>
							</div>
						</div>
					</div>

					<div class='columns is-centered'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<p>
								Suspensions can hurt students' chances for success.
							</p>
							<br>
							<p>
								When students are suspended they are more likely to struggle academically and more likely to be involved with the criminal justice system later in life. Research also shows that suspensions do not improve student behavior or overall school safety.
							</p>
							<br>
							<p>
								Some students are more likely to receive suspensions than others, despite evidence that students of different racial/ethnic backgrounds misbehave at similar rates.
							</p>
						</div>


						<div class='column is-8-widescreen is-6-tablet'>
							<div>
								<p class='chart-title has-text-pink-darkest'>Does this district suspend some students more than others?</p>
								<p class='chart-subtitle'>This chart shows the percentage of students receiving short-term suspensions compared to the percentage of students in this district</p>
								<EquityChart 
									metric="pct_sts_EQLOG" 
									:metricDescArr="[
										{'metric': 'pct_sts', 'text': 'suspensions'},
										{'metric': 'pct_students', 'text': 'students'}
									]"
									:width="innerWidth" 
									colorClass="has-text-pink-darkest"
								/>
								<div class='chart-footnote'>
									Suspension data is reported by the NC Department of Public Instruction for the 2017-18 school year.<a href="https://www.dpi.nc.gov/data-reports/dropout-and-discipline-data/discipline-alp-and-dropout-annual-reports"><sup>4</sup></a>
								</div>
								<div class='chart-footnote'>
									This chart only includes races and ethnicities for which the district has enough data to report. In some instances, it is impossible to know the exact number of suspensions for a group because of the way suspension data is reported by the state. When the exact number is not available, this report uses rough approximations. All approximations are equal to or slightly lower than the actual percentages.
								</div>
							</div>
						</div>

					</div>
				</div>
			</div>
		</div>

		<!-- crime -->
		<div id='profile-crime' class='profile-section'>
			<div class='container'>
				<div class='section'>
					<div class='columns'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<div class='profile-section-title-div' id='crime-title'>
								<p class='profile-h1'>Court Involvement</p>
								<img class='profile-icon filter-purple-darkest' src="@/assets/icon-court.svg" alt='Court by John T. Garcia from the Noun Project'>
							</div>
						</div>
					</div>

					<div class='columns is-centered'>
						<div class='column is-4-widescreen is-6-tablet profile-section-left-column'>
							<p> 
								Criminalization of youth behavior has significant consequences.
							</p>
							<br>
							<p>
								Studies show that youth who are referred to the criminal justice system are more likely to re-offend and to experience lifelong entanglement in the system. When youth are referred to the criminal justice system it can also impact their access to education, employment, housing, public benefits, voting rights, and other sources of opportunity and support.
							</p>
						</div>

						<div class='column is-8-widescreen is-6-tablet'>
							<div>
								<p class='chart-title has-text-purple-darkest'>Does this county refer some students to the criminal justice system more than others?</p>
								<p class='chart-subtitle'>This chart shows the percentage of students receiving a school-based juvenile-complaint compared to the percentage of students in this district</p>
								<EquityChart 
									metric="school_related_complaints_pct_EQLOG" 
									:metricDescArr="[
										{'metric': 'school_related_complaints_pct', 'text': 'complaints'},
										{'metric': 'pct_students', 'text': 'students'}
									]"
									:width="innerWidth" 
									colorClass="has-text-purple-dark"
								/>
								<div class='chart-footnote'>
									Juvenile complaint data is reported by the North Carolina Judicial Branch for the 2018-19 fiscal year.<a href="https://www.nccourts.gov/programs/school-justice-partnership/sjp-resources"><sup>5</sup></a>
								</div>
								<div class='chart-footnote'>
									This chart only includes races and ethnicities for which the county has enough data to report. Additionally, this chart only includes youth under 16 years old. Until December 2019, all 16- and 17-year-olds in North Carolina were automatically sent to the adult criminal system regardless of offense. This changed on December 1, 2019. Now, most youth under age 18 are sent to the juvenile system.
								</div>
							</div>
						</div>

					</div>
				</div>
			</div>
		</div>
	</div>
</template>	


<script>
	import { mapState } from 'vuex';

	import EquityChart from './EquityChart.vue';
	import BarChart from './BarChart.vue';


	export default {
		components: {
			EquityChart,
			BarChart
		},
		data() {
			return {
				innerWidth: 0
			}
		},
		mounted() {
			this.getChartWidth();
		},
		computed: {
			...mapState(['width'])
		},
		watch: {
			width() {
				this.getChartWidth();
			}
		},
		methods: {
			getChartWidth() {
				const el = document.querySelector('#chart-sizing-div');
				this.innerWidth = el.clientWidth;
			}
		}
	}
</script>